import Mention from '@tiptap/extension-mention';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';

export type MentionSuggestionItem = {
    id: string;
    label: string;
};

type MentionRenderProps = SuggestionProps<MentionSuggestionItem, MentionSuggestionItem>;

const POPUP_WIDTH = 240;
const POPUP_OFFSET = 8;
const POPUP_MAX_HEIGHT = 240;
const POPUP_PADDING = 6;
const VIEWPORT_MARGIN = 8;

const createMentionMenu = () => {
    let container: HTMLDivElement | null = null;
    let selectedIndex = 0;
    let currentProps: MentionRenderProps | null = null;
    let mountRoot: HTMLElement | null = null;
    let resetMountRootPosition: (() => void) | null = null;

    const removeContainer = () => {
        container?.remove();
        container = null;
        currentProps = null;
        selectedIndex = 0;
        resetMountRootPosition?.();
        resetMountRootPosition = null;
        mountRoot = null;
    };

    const selectItem = (index: number) => {
        if (!currentProps || !currentProps.items[index]) {
            return;
        }

        currentProps.command(currentProps.items[index]);
        removeContainer();
    };

    const updatePosition = () => {
        if (!container || !currentProps?.clientRect) {
            return;
        }

        const rect = currentProps.clientRect();
        if (!rect) {
            return;
        }

        if (mountRoot && mountRoot !== document.body) {
            const rootRect = mountRoot.getBoundingClientRect();
            const maxTop = mountRoot.scrollTop + mountRoot.clientHeight - container.offsetHeight - VIEWPORT_MARGIN;
            const maxLeft = mountRoot.scrollLeft + mountRoot.clientWidth - POPUP_WIDTH - VIEWPORT_MARGIN;
            const preferredTop = rect.bottom - rootRect.top + mountRoot.scrollTop + POPUP_OFFSET;
            const fallbackTop = rect.top - rootRect.top + mountRoot.scrollTop - container.offsetHeight - POPUP_OFFSET;
            const top = fallbackTop >= mountRoot.scrollTop + VIEWPORT_MARGIN ? fallbackTop : preferredTop;
            const left = rect.left - rootRect.left + mountRoot.scrollLeft;

            container.style.top = `${Math.max(mountRoot.scrollTop + VIEWPORT_MARGIN, Math.min(top, maxTop))}px`;
            container.style.left = `${Math.max(mountRoot.scrollLeft + VIEWPORT_MARGIN, Math.min(left, maxLeft))}px`;
            return;
        }

        const maxTop = window.innerHeight - container.offsetHeight - VIEWPORT_MARGIN;
        const maxLeft = window.innerWidth - POPUP_WIDTH - VIEWPORT_MARGIN;
        const preferredTop = rect.bottom + POPUP_OFFSET;
        const fallbackTop = rect.top - container.offsetHeight - POPUP_OFFSET;
        const top = fallbackTop >= VIEWPORT_MARGIN ? fallbackTop : preferredTop;

        container.style.top = `${Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop))}px`;
        container.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft))}px`;
    };

    const renderItems = () => {
        if (!container || !currentProps) {
            return;
        }

        container.replaceChildren();
        container.setAttribute('role', 'listbox');
        container.setAttribute('aria-label', 'Mention suggestions');

        currentProps.items.forEach((item, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = item.label;
            button.style.width = '100%';
            button.style.padding = '8px 10px';
            button.style.border = '0';
            button.style.background = index === selectedIndex ? 'rgba(15, 23, 42, 0.08)' : 'transparent';
            button.style.color = 'inherit';
            button.style.cursor = 'pointer';
            button.style.fontSize = '14px';
            button.style.textAlign = 'left';
            button.style.borderRadius = '6px';
            button.setAttribute('role', 'option');
            button.setAttribute('aria-selected', index === selectedIndex ? 'true' : 'false');
            button.addEventListener('mouseenter', () => {
                selectedIndex = index;
                renderItems();
            });
            button.addEventListener('pointerdown', (event) => {
                event.preventDefault();
                event.stopPropagation();
                selectItem(index);
            });
            container?.appendChild(button);
        });
    };

    const mount = (props: MentionRenderProps) => {
        currentProps = props;
        if (!props.items.length) {
            removeContainer();
            return;
        }

        selectedIndex = Math.min(selectedIndex, Math.max(props.items.length - 1, 0));

        if (!container) {
            mountRoot =
                (props.decorationNode instanceof HTMLElement &&
                    (props.decorationNode.closest('.minimal-tiptap-editor') ||
                        props.decorationNode.closest('[role="dialog"]'))) ||
                document.body;
            container = document.createElement('div');
            if (mountRoot !== document.body && getComputedStyle(mountRoot).position === 'static') {
                const previousPosition = mountRoot.style.position;
                mountRoot.style.position = 'relative';
                resetMountRootPosition = () => {
                    mountRoot!.style.position = previousPosition;
                };
            }

            container.style.position = mountRoot === document.body ? 'fixed' : 'absolute';
            container.style.zIndex = '9999';
            container.style.width = `${POPUP_WIDTH}px`;
            container.style.maxHeight = `${POPUP_MAX_HEIGHT}px`;
            container.style.overflowY = 'auto';
            container.style.padding = `${POPUP_PADDING}px`;
            container.style.border = '1px solid rgba(15, 23, 42, 0.12)';
            container.style.borderRadius = '10px';
            container.style.background = 'white';
            container.style.boxShadow = '0 16px 40px rgba(15, 23, 42, 0.12)';
            mountRoot.appendChild(container);
        }

        updatePosition();
        renderItems();
    };

    const onKeyDown = ({ event }: SuggestionKeyDownProps) => {
        if (!currentProps) {
            return false;
        }

        if (!currentProps.items.length) {
            return false;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            selectedIndex = (selectedIndex + 1) % currentProps.items.length;
            renderItems();
            return true;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            selectedIndex = (selectedIndex + currentProps.items.length - 1) % currentProps.items.length;
            renderItems();
            return true;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            selectItem(selectedIndex);
            return true;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            removeContainer();
            return true;
        }

        return false;
    };

    return {
        onStart: (props: MentionRenderProps) => mount(props),
        onUpdate: (props: MentionRenderProps) => mount(props),
        onKeyDown,
        onExit: removeContainer,
    };
};

export type MentionItemsSource = MentionSuggestionItem[] | (() => MentionSuggestionItem[]);

export const MinimalTiptapMention = (items: MentionItemsSource) =>
    Mention.configure({
        HTMLAttributes: {
            class: 'mention',
        },
        deleteTriggerWithBackspace: true,
        renderText: ({ node, options }) => `${options.suggestion.char}${node.attrs.label ?? node.attrs.id ?? ''}`,
        renderHTML: ({ node, options }) => [
            'span',
            {
                ...options.HTMLAttributes,
                'data-type': 'mention',
                'data-id': node.attrs.id,
                'data-label': node.attrs.label,
            },
            `${options.suggestion.char}${node.attrs.label ?? node.attrs.id ?? ''}`,
        ],
        suggestion: {
            char: '@',
            items: ({ query }) => {
                const resolved = typeof items === 'function' ? items() : items;
                const normalizedQuery = query.trim().toLowerCase();
                return resolved.filter((item) => item.label.toLowerCase().includes(normalizedQuery)).slice(0, 8);
            },
            render: createMentionMenu,
        },
    });
